import React, { useEffect, useState } from 'react';
import { TableHead, Th, Tr, Td } from '../../Elements/DataTable';
import { HiOutlineDotsHorizontal, HiOutlinePencilAlt, HiOutlineTrash, HiOutlineBan, HiOutlineCreditCard } from 'react-icons/hi';
import { getImageCacheKey, getImageUrl } from '../../../lib/helpers';
import { getShopBilling, TONE_CLASSES, VENUE_TYPE_LABELS } from '../../../lib/shopBilling';
import { ownersSummary } from '../../../lib/shopOwners';
import ShopBillingBlock from './ShopBillingBlock';

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatAddress(shop) {
  return [shop.address, [shop.postal_code, shop.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ') || '—';
}

function ShopAvatar({ shop, size = 'h-9 w-9' }) {
  const [broken, setBroken] = useState(false);
  const url = getImageUrl(shop.image_path, { cacheKey: getImageCacheKey('venue', shop.id) });
  if (url && !broken) {
    return (
      <img
        src={url}
        alt=""
        className={`${size} shrink-0 rounded object-cover`}
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div className={`${size} shrink-0 rounded bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-400`}>
      {shop.name?.charAt(0)?.toUpperCase() || 'T'}
    </div>
  );
}

function RowMenu({ shop, billing, onManageBilling, onCancelSubscription, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex justify-end">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Más acciones para ${shop.name || 'la tienda'}`}
        aria-expanded={open}
        className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
      >
        <HiOutlineDotsHorizontal className="text-lg" aria-hidden />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-full z-20 mt-1 w-60 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
            <button
              type="button"
              onClick={() => { setOpen(false); onManageBilling?.(shop.id); }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
            >
              <HiOutlineCreditCard className="text-base text-gray-400" aria-hidden />
              Gestionar facturación
            </button>
            {billing.hasSubscription && (
              <button
                type="button"
                onClick={() => { setOpen(false); onCancelSubscription?.(shop.id); }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
              >
                <HiOutlineBan className="text-base" aria-hidden />
                Cancelar suscripción
              </button>
            )}
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete?.(shop.id); }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
            >
              <HiOutlineTrash className="text-base" aria-hidden />
              Eliminar tienda
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Tabla de tiendas — rediseño de Figma (`Club_ShopTableHeader` / `Club_ShopRow`).
 *
 * Columnas: Tienda · Dirección · Contacto · Facturación · Propietarios · Alta · Acciones.
 * El bloque de Stripe ya no está duplicado aquí: en escritorio la columna
 * Facturación resume estado y plan, y la gestión vive en el menú «⋯». La lista
 * de móvil sí lo monta entero, porque ahí no hay columnas donde repartirlo.
 */
const ShopTable = ({
  shops,
  loading,
  onEdit,
  onDelete,
  onToggleSelect,
  onToggleSelectAll,
  selectedIds = [],
  onManageBilling,
  onCancelSubscription,
  onStartBilling,
  onStartSetup,
}) => {
  const [selectedPlans, setSelectedPlans] = useState({});

  useEffect(() => {
    if (!shops) return;
    setSelectedPlans((prev) => {
      const next = { ...prev };
      shops.forEach((shop) => {
        if (!next[shop.id]) next[shop.id] = shop.plan_tier || 'basico';
      });
      return next;
    });
  }, [shops]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-emerald-600" />
          <p className="text-gray-500">Cargando tiendas…</p>
        </div>
      </div>
    );
  }

  if (!shops || shops.length === 0) return null;

  const allVisibleSelected =
    shops.length > 0 && shops.every((s) => selectedIds.some((id) => String(id) === String(s.id)));

  const runAction = (shop) => (kind, plan) => {
    if (kind === 'setup') onStartSetup?.(shop.id, plan);
    else if (kind === 'subscribe') onStartBilling?.(shop.id, plan);
    else onManageBilling?.(shop.id);
  };

  return (
    <>
      {/* Listado responsive: móvil (vertical Y apaisado) + tablet vertical.
          Antes el apaisado 768–1023px (portátiles grandes en horizontal, no
          solo tablets) forzaba la tabla de 8 columnas — no cabía ni truncada
          (~836px de tabla contra ~538px de hueco real) y quedaba con scroll
          feo. Se le da el mismo listado en tarjetas que ya usa el resto de
          móvil: se ve todo, sin recortar ningún dato. Solo `lg:` (≥1024px,
          desktop y tablet apaisada real 1280×800) sigue usando la tabla. */}
      <div className="block space-y-3 lg:hidden">
        {shops.map((shop) => {
          const billing = getShopBilling(shop);
          const owners = ownersSummary(shop.owners);
          return (
            <div key={shop.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={selectedIds.some((id) => String(id) === String(shop.id))}
                  onChange={() => onToggleSelect(shop.id)}
                  className="mt-1 h-4 w-4 accent-emerald-600"
                  aria-label={`Seleccionar ${shop.name || 'tienda'}`}
                />
                <ShopAvatar shop={shop} size="h-11 w-11" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-gray-900">{shop.name || '—'}</div>
                      <div className="text-xs text-gray-500">
                        {VENUE_TYPE_LABELS[shop.venue_type] || shop.venue_type || '—'}
                      </div>
                    </div>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${TONE_CLASSES[billing.tone]}`}>
                      {billing.label}
                    </span>
                  </div>
                </div>
              </div>

              <dl className="mt-3 space-y-1 text-xs">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">Dirección</dt>
                  <dd className="text-right text-gray-900">{formatAddress(shop)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">Contacto</dt>
                  <dd className="text-right text-gray-900">{shop.email || shop.phone || '—'}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">Propietarios</dt>
                  <dd className="text-right text-gray-900">
                    {owners || <span className="italic text-gray-400">Sin asignar</span>}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">Alta</dt>
                  <dd className="text-right text-gray-900">{fmtDate(shop.date_created)}</dd>
                </div>
              </dl>

              <div className="mt-3">
                <ShopBillingBlock
                  billing={billing}
                  selectedPlan={selectedPlans[shop.id] || billing.planTier}
                  onSelectPlan={(value) => setSelectedPlans((prev) => ({ ...prev, [shop.id]: value }))}
                  onAction={runAction(shop)}
                  compact
                />
              </div>

              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onEdit(shop.id)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
                >
                  <HiOutlinePencilAlt className="text-base" aria-hidden />
                  Editar
                </button>
                <RowMenu
                  shop={shop}
                  billing={billing}
                  onManageBilling={onManageBilling}
                  onCancelSubscription={onCancelSubscription}
                  onDelete={onDelete}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabla: solo desktop y tablet apaisada real (≥1024px, ver el bloque
          de arriba) */}
      <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white lg:block">
        <table className="w-full text-left text-gray-700">
          <TableHead>
              <Th className="w-12">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={() => onToggleSelectAll?.()}
                  className="h-4 w-4 accent-emerald-600"
                  aria-label="Seleccionar todas las tiendas de la página"
                />
              </Th>
              <Th>Tienda</Th>
              <Th>Dirección</Th>
              <Th>Contacto</Th>
              <Th>Facturación</Th>
              <Th>Propietarios</Th>
              <Th>Alta</Th>
              <Th align="right"><span className="sr-only">Acciones</span></Th>
          </TableHead>
          <tbody>
            {shops.map((shop) => {
              const billing = getShopBilling(shop);
              const owners = ownersSummary(shop.owners);
              const selected = selectedIds.some((id) => String(id) === String(shop.id));
              return (
                <Tr
                  key={shop.id}
                  className={`border-b border-gray-100 last:border-0 ${selected ? 'bg-emerald-50/50' : 'hover:bg-gray-50'}`}
                >
                  <Td>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => onToggleSelect(shop.id)}
                      className="h-4 w-4 accent-emerald-600"
                      aria-label={`Seleccionar ${shop.name || 'tienda'}`}
                    />
                  </Td>
                  <Td>
                    <div className="flex items-center gap-3">
                      <ShopAvatar shop={shop} />
                      <div className="min-w-0">
                        <div className="truncate font-medium text-gray-900">{shop.name || '—'}</div>
                        <div className="text-xs text-gray-500">
                          {VENUE_TYPE_LABELS[shop.venue_type] || shop.venue_type || '—'}
                        </div>
                      </div>
                    </div>
                  </Td>
                  <Td className="text-sm">{formatAddress(shop)}</Td>
                  <Td className="text-sm">
                    <div>{shop.email || '—'}</div>
                    {shop.phone && <div className="text-xs text-gray-500">{shop.phone}</div>}
                  </Td>
                  <Td>
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${TONE_CLASSES[billing.tone]}`}>
                      {billing.label}
                    </span>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {billing.canPickPlan && billing.state === 'not_started' ? 'Sin plan' : `Plan ${billing.planLabel}`}
                    </div>
                  </Td>
                  <Td className="text-sm">
                    {owners || <span className="text-xs italic text-gray-400">Sin asignar</span>}
                  </Td>
                  <Td className="text-sm text-gray-500">{fmtDate(shop.date_created)}</Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onEdit(shop.id)}
                        aria-label={`Editar ${shop.name || 'tienda'}`}
                        className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
                      >
                        <HiOutlinePencilAlt className="text-lg" aria-hidden />
                      </button>
                      <RowMenu
                        shop={shop}
                        billing={billing}
                        onManageBilling={onManageBilling}
                        onCancelSubscription={onCancelSubscription}
                        onDelete={onDelete}
                      />
                    </div>
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ShopTable;
